import classnamesBind from 'classnames/bind';
import type { FC, ReactNode } from 'react';
import { memo, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { Link } from '../../components/Link';
import type { ThemeColors } from '../../types/theme';

export type CategoryPropTypes = {
  slug?: string | null;
  name?: string | null;
  title?: string | null;
  className?: string;
  color?: ThemeColors;
  rootTags?: boolean;
  variant?: 'contained' | 'outlined';
  tooltip?: ReactNode;
  icon?: ReactNode;
  disableLink?: boolean;
  isGroup?: boolean;
  // B4_05 — navigation par facette : route la pill vers /theme/<slug> ou /entity/<slug>
  // au lieu de la page de catégorie /[tag]. Absent → comportement historique inchangé.
  facetType?: 'theme' | 'entity';
};

interface IBtnClasses {
  base: string;
  contained: string;
}

const primaryClasses: IBtnClasses = {
  base: 'text-base text-red-350',
  contained: 'text-base text-red-350 bg-red-350 hover:text-white',
};

const grayClasses: IBtnClasses = {
  base: 'text-base text-gray',
  contained: 'text-base text-gray-medium bg-gray-medium hover:text-white',
};

export const CategoryBadge: FC<CategoryPropTypes> = memo(function CategoryBadge(props) {
  const {
    slug,
    name,
    title,
    className,
    color,
    tooltip,
    rootTags,
    variant,
    icon,
    disableLink,
    facetType,
  } = props;

  // Cible de route (B4_05) : une pill de facette pointe sa page dédiée
  // /theme/<slug> ou /entity/<slug> (params {slug}). Sinon comportement historique :
  // page de catégorie /[tag] (params {tag}), ou liste des tags (rootTags → /tags).
  const linkTo = facetType ?? (rootTags ? '/tags' : 'category');
  const linkParams = useMemo(() => {
    if (facetType) return { slug };
    return rootTags ? undefined : { tag: slug };
  }, [facetType, rootTags, slug]);

  const styles = color === 'primary' ? { ...primaryClasses } : { ...grayClasses };

  const cx = classnamesBind.bind(styles);
  const contained = variant === 'contained';

  const classes = twMerge(
    'flex items-center justify-center py-1 px-3 break-words rounded-3xl cursor-pointer text-sm text-center font-normal tracking-4',
    variant === 'contained' && 'bg-opacity-10 hover:bg-opacity-100',
    cx({ base: !contained }, { contained }),
    className,
  );

  const content = (
    <>
      {icon && <span className="">{icon}</span>}
      <span>{title || name || slug}</span>
      {tooltip && tooltip}
    </>
  );

  if (disableLink) {
    return <div className={classes}>{content}</div>;
  }

  return (
    <Link to={linkTo} params={linkParams}>
      <a className={classes}>{content}</a>
    </Link>
  );
});
